const doctorImagePrimary = new URL('../assets/images/articles-dr-enrique.png', import.meta.url).href;
const doctorImageSecondary = new URL('../assets/images/blog-dr-image1.png', import.meta.url).href;

type Review = {
  id: string;
  name: string;
  location: string;
  rating: number;
  text: string;
  image: string;
};

type ScheduleEntry = {
  date: string;
  times: string[];
};

type DoctorAddress = {
  id: string;
  name: string;
  location: string;
  icon: string | null;
  iconClass: string;
  schedule: ScheduleEntry[];
};

export interface MockDoctor {
  id: string;
  name: string;
  specialty: string;
  image: string;
  appointmentDate: string;
  mainAddress: string;
  paymentMethod: string;
  services: string[];
  aboutMe: string[];
  consultationFee: number;
  reviews: Review[];
  addresses: DoctorAddress[];
  position?: {
    lat: number;
    lng: number;
  };
}

type Appointment = {
  id: string;
  doctor: MockDoctor;
};

const baseReviews: Review[] = [
  {
    id: 'review-1',
    name: 'Laura M.',
    location: 'Barcelona',
    rating: 4.9,
    text: 'Excelente atención y seguimiento durante todo el proceso.',
    image: doctorImagePrimary,
  },
  {
    id: 'review-2',
    name: 'Jordi P.',
    location: 'Madrid',
    rating: 4.7,
    text: 'Trato muy humano y cercano. Me sentí escuchado en todo momento.',
    image: doctorImageSecondary,
  },
  {
    id: 'review-3',
    name: 'María L.',
    location: 'Valencia',
    rating: 5,
    text: 'Resultados visibles desde la primera consulta. Muy recomendable.',
    image: doctorImagePrimary,
  },
];

const createSchedule = (): ScheduleEntry[] => {
  const baseDates = [
    '2024-07-05',
    '2024-07-06',
    '2024-07-07',
    '2024-07-08',
    '2024-07-09',
    '2024-07-10',
    '2024-07-11',
    '2024-07-12',
  ];

  const baseTimes = [
    ['09:00', '09:30', '10:00', '10:30'],
    ['11:00', '11:30', '12:00', '12:30'],
    ['13:00', '13:30', '14:00', '14:30'],
    ['15:00', '15:30', '16:00', '16:30'],
  ];

  return baseDates.map((date, index) => ({
    date,
    times: baseTimes[index % baseTimes.length].map(time => time),
  }));
};

const createAddresses = (overrides?: Partial<DoctorAddress>[]): DoctorAddress[] => {
  const defaults: DoctorAddress[] = [
    {
      id: 'addr-online',
      name: 'Online',
      location: 'Videoconsulta',
      icon: null,
      iconClass: '',
      schedule: createSchedule(),
    },
    {
      id: 'addr-clinic',
      name: 'Consultorio',
      location: 'Carrer de Mallorca 123, Barcelona',
      icon: null,
      iconClass: '',
      schedule: createSchedule(),
    },
  ];

  if (!overrides || overrides.length === 0) {
    return defaults.map(address => ({
      ...address,
      schedule: address.schedule.map(entry => ({
        ...entry,
        times: entry.times.map(time => time),
      })),
    }));
  }

  return overrides.map((override, index) => {
    const template = defaults[index % defaults.length];
    const schedule = override.schedule ?? template.schedule;

    return {
      ...template,
      ...override,
      schedule: schedule.map(entry => ({
        ...entry,
        times: entry.times.map(time => time),
      })),
    };
  });
};

const cloneReviews = (reviews: Review[]): Review[] => reviews.map(review => ({ ...review }));

const createDoctor = (options: Partial<MockDoctor> & { id: string; name: string; specialty: string }): MockDoctor => ({
  id: options.id,
  name: options.name,
  specialty: options.specialty,
  image: options.image ?? doctorImagePrimary,
  appointmentDate: options.appointmentDate ?? 'Lunes, 09:00',
  mainAddress: options.mainAddress ?? 'Carrer de Mallorca 123, Barcelona',
  paymentMethod: options.paymentMethod ?? 'Tarjeta y Bizum',
  services: options.services ? [...options.services] : [
    'Consulta inicial de valoración',
    'Seguimiento personalizado',
    'Plan de bienestar a medida',
  ],
  aboutMe: options.aboutMe ? [...options.aboutMe] : [
    'Profesional con más de 10 años de experiencia en medicina integrativa.',
    'Enfoque centrado en la persona con acompañamiento integral.',
  ],
  consultationFee: options.consultationFee ?? 80,
  reviews: options.reviews ? cloneReviews(options.reviews) : cloneReviews(baseReviews),
  addresses: createAddresses(options.addresses),
  position: options.position ? { ...options.position } : { lat: 41.3879, lng: 2.16992 },
});

export const mockDoctors: MockDoctor[] = [
  createDoctor({
    id: 'doctor-1',
    name: 'Dra. Ana García',
    specialty: 'Medicina Integrativa',
    appointmentDate: 'Lunes, 09:00',
    position: { lat: 41.3879, lng: 2.16992 },
  }),
  createDoctor({
    id: 'doctor-2',
    name: 'Dr. Carlos Pérez',
    specialty: 'Fisioterapia Deportiva',
    image: doctorImageSecondary,
    appointmentDate: 'Martes, 11:00',
    mainAddress: 'Calle Alcalá 45, Madrid',
    position: { lat: 40.4168, lng: -3.7038 },
    services: [
      'Tratamiento de lesiones deportivas',
      'Rehabilitación funcional',
      'Plan preventivo personalizado',
    ],
  }),
  createDoctor({
    id: 'doctor-3',
    name: 'Dra. Laura Ruiz',
    specialty: 'Nutrición Funcional',
    appointmentDate: 'Miércoles, 16:00',
    mainAddress: 'Avenida del Puerto 21, Valencia',
    position: { lat: 39.4699, lng: -0.3763 },
    services: [
      'Evaluación nutricional completa',
      'Planes alimenticios personalizados',
      'Seguimiento quincenal',
    ],
  }),
];

const cloneDoctor = (doctor: MockDoctor): MockDoctor => ({
  ...doctor,
  services: [...doctor.services],
  aboutMe: [...doctor.aboutMe],
  reviews: cloneReviews(doctor.reviews),
  addresses: doctor.addresses.map(address => ({
    ...address,
    schedule: address.schedule.map(entry => ({
      ...entry,
      times: entry.times.map(time => time),
    })),
  })),
  position: doctor.position ? { ...doctor.position } : undefined,
});

export const mockFeaturedDoctors: MockDoctor[] = mockDoctors.map(cloneDoctor);

export const mockClientReviews: Review[] = cloneReviews(baseReviews);

const createAppointments = (prefix: string): Appointment[] => mockDoctors.map((doctor, index) => ({
  id: `${prefix}-${index + 1}`,
  doctor: cloneDoctor(doctor),
}));

export const mockUpcomingAppointments: Appointment[] = createAppointments('upcoming');
export const mockPastAppointments: Appointment[] = createAppointments('past');
export const mockFavoriteAppointments: Appointment[] = createAppointments('favorite');

export const findMockDoctorById = (id: string | number): MockDoctor | undefined => {
  const idAsString = String(id);
  const found = mockDoctors.find(doctor => doctor.id === idAsString);
  return found ? cloneDoctor(found) : undefined;
};
